import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { formatVendorStartingPrice } from '../utils/pricing';
import { formatLocationHeader } from '../utils/locationFormat';
import { useAppLocation, STATE_ABBR } from '../contexts/LocationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import SearchableSelect from '../components/ui/searchable-select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import {
  Sparkles, MapPin, Star, X, Check, Clock, ArrowLeft,
  CheckCircle, ShieldCheck, SlidersHorizontal, Locate, Search, ChevronDown
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { BadgeList } from '../components/BadgeDisplay';
import BottomTabBar from '../components/BottomTabBar';
import { VendorGridSkeleton } from '../components/skeletons';
import { getHeroImage } from '../utils/vendorImage';

const BrowseVendors = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, activeRole } = useAuth();
  const appLoc = useAppLocation();

  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [states, setStates] = useState([]);
  const [filters, setFilters] = useState({
    city: '', state: '', category: '', group: '', zip_code: '',
    same_day: false, next_day: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [vendorBadges, setVendorBadges] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [locationApplied, setLocationApplied] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [tempLocation, setTempLocation] = useState({ zip: '', city: '', state: '' });

  const debouncedFetchRef = useRef(null);
  const initialLoadDone = useRef(false);

  const eventId = searchParams.get('eventId');
  const returnToEvent = searchParams.get('returnToEvent') === 'true';
  const urlZip = searchParams.get('zip');
  const urlCity = searchParams.get('city');
  const urlState = searchParams.get('state');

  // ─── Auto-fetch on filter change (debounced) ───
  useEffect(() => {
    if (!initialLoadDone.current) return;
    clearTimeout(debouncedFetchRef.current);
    debouncedFetchRef.current = setTimeout(() => fetchVendors(), 350);
    return () => clearTimeout(debouncedFetchRef.current);
  }, [filters, searchQuery]);

  useEffect(() => {
    if (user && (activeRole === 'customer' || (!activeRole && user.role === 'customer'))) {
      localStorage.setItem('celey:lastRoute:customer', '/browse');
    }
  }, [user, activeRole]);

  // URL params → apply immediately
  useEffect(() => {
    if (urlZip || urlCity) {
      const loc = { city: urlCity || '', state: urlState || '', zip: urlZip || '' };
      setFilters(prev => ({ ...prev, city: loc.city, state: loc.state, zip_code: loc.zip }));
      appLoc.setAppLocation({ ...loc, source: 'selected' });
      setLocationApplied(true);
    }
  }, []);

  useEffect(() => { fetchData(); }, []);

  // ─── Sync location from context to filters ───
  const applyLocation = (city, state, zip, source) => {
    setFilters(prev => ({ ...prev, city: city || '', state: state || '', zip_code: zip || '' }));
    appLoc.setAppLocation({ city: city || '', state: state || '', zip: zip || '', source: source || 'selected' });
    setLocationApplied(true);
  };

  const handleDetectLocation = async () => {
    const result = await appLoc.detectLocation();
    if (result) {
      setFilters(prev => ({ ...prev, city: result.city || '', state: result.state || '' }));
      setLocationApplied(true);
    }
  };

  // ─── Auto-location on mount ───
  const attemptAutoLocation = async (isPresMode) => {
    // User explicitly dismissed location this session — respect it
    if (appLoc.isLocationDismissed()) return;

    // Presentation mode → LA
    if (isPresMode) {
      applyLocation('', 'CA', '', 'selected');
      appLoc.setAppLocation({ city: 'Los Angeles', state: 'CA', zip: '', source: 'selected' });
      return;
    }

    // If shared context already has location (from previous visit)
    if (appLoc.hasLocation) {
      setFilters(prev => ({
        ...prev,
        city: appLoc.location.city || '',
        state: appLoc.location.state || '',
        zip_code: appLoc.location.zip || ''
      }));
      setLocationApplied(true);
      return;
    }

    // Check user's saved preference
    if (user) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/customers/me/location-preference`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { default_location_city, default_location_state, default_location_zip } = res.data;
        if (default_location_city || default_location_state || default_location_zip) {
          applyLocation(
            default_location_city || '',
            default_location_state || '',
            default_location_zip || '',
            'selected'
          );
          return;
        }
      } catch (e) { /* no saved preference */ }
    }

    // Browser geolocation
    handleDetectLocation();
  };

  // ─── Data fetching ───
  const saveLocationPreference = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/customers/me/location-preference`, {
        default_location_zip: tempLocation.zip || null,
        default_location_city: tempLocation.city || null,
        default_location_state: tempLocation.state || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      applyLocation(tempLocation.city, tempLocation.state, tempLocation.zip, 'selected');
      setShowLocationModal(false);
    } catch (e) { console.error('Error saving location preference:', e); }
  };

  const clearLocationFilter = () => {
    setFilters(prev => ({ ...prev, city: '', state: '', zip_code: '' }));
    setLocationApplied(false);
    appLoc.clearLocation();
  };

  const openLocationModal = () => {
    setTempLocation({
      zip: appLoc.location.zip || '',
      city: appLoc.location.city || '',
      state: appLoc.location.state || ''
    });
    setShowLocationModal(true);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath.trim() === '') return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${backendUrl}${cleanPath}`;
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, statesRes, presRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/states`),
        axios.get(`${API}/presentation-mode`).catch(() => ({ data: { presentation_mode: false } }))
      ]);
      setCategories(categoriesRes.data.categories.map(c => ({ value: c, label: c })));
      setCategoryGroups(categoriesRes.data.groups || []);
      setStates(statesRes.data.states.map(s => ({ value: STATE_ABBR[s] || s, label: s })));

      if (urlZip || urlCity) {
        await fetchVendors({
          city: urlCity || '', state: urlState || '', zip_code: urlZip || '',
          category: '', group: '', same_day: false, next_day: false
        });
      } else {
        await fetchVendors();
      }
      initialLoadDone.current = true;

      if (!urlCity && !urlZip) {
        attemptAutoLocation(presRes.data.presentation_mode);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchVendors = async (customFilters) => {
    const f = customFilters || filters;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.city) params.append('city', f.city);
      if (f.state) params.append('state', f.state);
      if (f.category) params.append('category', f.category);
      if (f.group && !f.category) params.append('group', f.group);
      if (f.zip_code) params.append('zip_code', f.zip_code);
      if (f.same_day) params.append('same_day', 'true');
      if (f.next_day) params.append('next_day', 'true');
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const res = await axios.get(`${API}/vendors?${params.toString()}`);
      const visibleVendors = res.data.filter(v => v.is_visible !== false);
      setVendors(visibleVendors);

      const badgePromises = visibleVendors.map(async (vendor) => {
        try {
          const badgesRes = await axios.get(`${API}/vendors/${vendor.id}/badges`);
          return { vendorId: vendor.id, badges: badgesRes.data };
        } catch { return { vendorId: vendor.id, badges: [] }; }
      });
      const badgeResults = await Promise.all(badgePromises);
      const badgesMap = {};
      badgeResults.forEach(r => { badgesMap[r.vendorId] = r.badges; });
      setVendorBadges(badgesMap);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Filter handlers ───
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      // Detailed category and broad group are mutually exclusive filters
      if (key === 'category' && value) next.group = '';
      return next;
    });
    if (['city', 'state', 'zip_code'].includes(key) && locationApplied) {
      setLocationApplied(false);
    }
  };

  const handleGroupChipClick = (value) => {
    setFilters(prev => ({ ...prev, group: value, category: '' }));
  };

  const handleClearFilters = () => {
    setFilters({ city: '', state: '', category: '', group: '', zip_code: '', same_day: false, next_day: false });
    setLocationApplied(false);
    appLoc.clearLocation();
  };

  // ─── Helpers ───
  const getAvailabilityBadge = (vendor) => {
    if (vendor.allow_same_day) return { text: 'Same-Day Booking', color: 'bg-emerald-100 text-emerald-800' };
    if (vendor.allow_next_day) return { text: 'Next-Day Booking', color: 'bg-blue-100 text-blue-800' };
    const days = Math.floor((vendor.min_notice_hours || 0) / 24);
    if (days > 0) return { text: `Min ${days} Day${days > 1 ? 's' : ''} Notice`, color: 'bg-gray-100 text-gray-700' };
    return null;
  };

  const formatPrice = (price) => {
    if (!price) return null;
    if (Array.isArray(price)) {
      if (price.length === 0) return null;
      const raw = String(price[0] || '').trim();
      if (!raw || raw === '0' || raw === '$' || raw === 'Contact for pricing') return null;
      const rm = raw.match(/\$?([\d,]+)\s*[-–]\s*\$?([\d,]+)/);
      if (rm) return `Starting at $${rm[1]}`;
      const um = raw.match(/^Under\s*\$?([\d,]+)/i);
      if (um) return `Starting at $${um[1]}`;
      const nm = raw.match(/^\$?([\d,]+)/);
      if (nm) return `Starting at $${nm[1]}`;
      return raw;
    }
    const s = String(price).trim();
    if (!s || s === '0' || s === '$' || s === 'Contact for pricing') return null;
    const rm = s.match(/\$?([\d,]+)\s*[-–]\s*\$?([\d,]+)/);
    if (rm) return `Starting at $${rm[1]}`;
    const um = s.match(/^Under\s*\$?([\d,]+)/i);
    if (um) return `Starting at $${um[1]}`;
    const nm = s.match(/^\$?([\d,]+)/);
    if (nm) return `Starting at $${nm[1]}`;
    return s;
  };

  const activeFilterCount = [filters.city, filters.state, filters.zip_code, filters.same_day, filters.next_day].filter(Boolean).length;
  // Chip rail: All + most useful broad groups inline; the rest live behind a
  // compact "More" menu so native width never becomes a long scrolling rail.
  const PRIMARY_GROUP_COUNT = 4;
  const primaryGroupChips = categoryGroups.slice(0, PRIMARY_GROUP_COUNT).map(g => ({ value: g.name, label: g.name }));
  const moreGroupChips = categoryGroups.slice(PRIMARY_GROUP_COUNT).map(g => ({ value: g.name, label: g.name }));
  const categoryChips = [{ value: '', label: 'All' }, ...primaryGroupChips];
  const activeMoreGroup = moreGroupChips.find(g => g.value === filters.group && !filters.category);
  const locationDisplay = appLoc.formatDisplay();
  // Render-time safety net — even if a stale admin label sits in localStorage,
  // the header degrades gracefully (city → city+state → state → "your area").
  const headerText = locationApplied
    ? formatLocationHeader({ city: appLoc.location.city, state: appLoc.location.state })
        .replace(/^Near /, 'Providers near ')
        .replace(/^In /, 'Providers in ')
    : 'Explore providers';
  const trustSignal = (!loading && vendors.length > 0)
    ? locationApplied
      ? `${vendors.length} provider${vendors.length === 1 ? '' : 's'} in your area`
      : `${vendors.length} provider${vendors.length === 1 ? '' : 's'}`
    : null;

  // ─── Location pill for mobile toolbar ───
  const LocationPill = () => {
    if (locationApplied) {
      return (
        <button
          onClick={clearLocationFilter}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-200"
          data-testid="mobile-location-active"
        >
          <MapPin className="h-3 w-3" />
          {locationDisplay}
          <X className="h-3 w-3 ml-0.5" />
        </button>
      );
    }
    if (appLoc.geoStatus === 'detecting') {
      return (
        <button
          onClick={appLoc.cancelDetection}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 text-gray-500 text-xs rounded-full hover:bg-gray-200 transition-colors animate-pulse"
          data-testid="mobile-location-detecting"
          aria-label="Cancel detecting location"
        >
          <Locate className="h-3 w-3 animate-spin" />
          Detecting... (tap to cancel)
        </button>
      );
    }
    return (
      <button
        onClick={handleDetectLocation}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 transition-colors"
        data-testid="mobile-use-location"
      >
        <Locate className="h-3 w-3" />
        Use your location
      </button>
    );
  };

  // ─── Shared Filter Panel ───
  const FilterPanel = ({ isMobile }) => (
    <div className="space-y-6">
      {/* Location */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">Location</p>

        {!locationApplied && appLoc.geoStatus !== 'detecting' && (
          <button
            onClick={handleDetectLocation}
            className="mb-3 inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
            data-testid="detect-location-btn"
          >
            <Locate className="h-3 w-3" />
            Detect my location
          </button>
        )}
        {appLoc.geoStatus === 'detecting' && (
          <button
            type="button"
            onClick={appLoc.cancelDetection}
            className="mb-3 inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 animate-pulse"
            data-testid="detect-location-cancel-btn"
          >
            <Locate className="h-3 w-3 animate-spin" />
            Detecting... (tap to cancel)
          </button>
        )}

        <div className="space-y-2.5">
          <Input
            placeholder="City"
            value={filters.city}
            onChange={(e) => handleFilterChange('city', e.target.value)}
            data-testid="filter-city"
            className="h-9 text-sm"
          />
          <SearchableSelect
            options={states}
            value={filters.state}
            onValueChange={(v) => handleFilterChange('state', v)}
            placeholder="State"
            testId="filter-state"
          />
          <Input
            placeholder="Zip code"
            value={filters.zip_code}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              handleFilterChange('zip_code', v);
            }}
            data-testid="filter-zip"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Category — desktop only. Mobile taxonomy discovery = universal
          search + broad group chips (the SearchableSelect dropdown is
          obscured by the iOS keyboard inside the bottom Sheet). */}
      {!isMobile && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">Category</p>
          <SearchableSelect
            options={categories}
            value={filters.category}
            onValueChange={(v) => handleFilterChange('category', v)}
            placeholder="All categories"
            testId="filter-category"
          />
        </div>
      )}

      {/* Availability */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">Availability</p>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Same-Day</span>
            <Switch
              checked={filters.same_day}
              onCheckedChange={(v) => handleFilterChange('same_day', v)}
              data-testid="filter-same-day"
            />
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Next-Day</span>
            <Switch
              checked={filters.next_day}
              onCheckedChange={(v) => handleFilterChange('next_day', v)}
              data-testid="filter-next-day"
            />
          </label>
        </div>
      </div>

      {/* Clear filters */}
      {(filters.city || filters.state || filters.zip_code || filters.category || filters.group || filters.same_day || filters.next_day) && (
        <button
          onClick={() => { handleClearFilters(); if (isMobile) setMobileFiltersOpen(false); }}
          className="text-xs text-gray-400 hover:text-gray-600 font-medium"
          data-testid="clear-all-filters"
        >
          Clear filters
        </button>
      )}
    </div>
  );

  // ─── Vendor Card ───
  const VendorCard = ({ vendor }) => {
    // Phase-5-alignment: canonical pricing (packages → à-la-carte → bucket).
    // Same helper used by VendorDetail + PlanEvent so the price shown on
    // the browse card matches the price shown on the detail hero.
    const displayPrice = formatVendorStartingPrice(vendor, formatPrice);
    const hasBadges = vendorBadges[vendor.id]?.length > 0 || getAvailabilityBadge(vendor);

    return (
      <Card
        className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-white border-gray-100"
        onClick={() => {
          const base = `/vendors/${vendor.id}`;
          navigate(eventId && returnToEvent ? `${base}?eventId=${eventId}&returnToEvent=true` : `${base}?fromBrowse=true`);
        }}
        data-testid="vendor-card"
      >
        <div className="aspect-[4/3] relative overflow-hidden">
          {(() => {
            // Shared hero chain: cover_image → portfolio[0] → profile_photo
            const cardImage = getHeroImage(vendor);
            return cardImage ? (
              <img
                src={getImageUrl(cardImage)}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null;
          })()}
          <div
            className={`w-full h-full bg-gradient-to-br from-emerald-400 to-emerald-600 items-center justify-center absolute inset-0 ${
              getHeroImage(vendor) ? 'hidden' : 'flex'
            }`}
          >
            <Sparkles className="h-12 w-12 text-white/80" />
          </div>
          <span className="absolute top-2.5 left-2.5 bg-black/50 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full shadow-sm">
            {vendor.display_category || vendor.category || (vendor.categories && vendor.categories[0]) || 'Service Provider'}
          </span>
          {/* Phase 5C: subtle hint when the vendor surfaced via a services[]
              match rather than their categories. Only appears when the
              backend feature flag is on AND matched_by='service'. */}
          {vendor.matched_by === 'service' && vendor.matched_service_name && (
            <span
              className="absolute top-2.5 right-2.5 bg-sky-500/85 backdrop-blur-md text-white text-[10px] font-medium px-2 py-0.5 rounded-full shadow-sm"
              title={`Matched by offering: ${vendor.matched_service_name}`}
              data-testid="matched-by-service-badge"
            >
              Matched by offering
            </span>
          )}
        </div>

        <CardContent className="p-3 sm:p-4">
          <h3 className="font-semibold text-gray-900 text-base leading-tight truncate mb-1">
            {vendor.business_name}
          </h3>
          <div className="flex items-center text-gray-400 text-xs">
            <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate">{vendor.city}{vendor.city && vendor.state ? ', ' : ''}{vendor.state}</span>
          </div>

          <div className={`${hasBadges || vendor.profile_strength_badge ? 'mt-3 pt-2 border-t border-gray-100' : 'mt-2'}`}>
            {(hasBadges || vendor.profile_strength_badge) && (
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {vendor.profile_strength_badge === 'ready_to_book' && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-ready-to-book">
                    <CheckCircle className="h-2.5 w-2.5" /> Ready to Book
                  </span>
                )}
                {vendor.profile_strength_badge === 'verified_provider' && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid="badge-verified-provider">
                    <ShieldCheck className="h-2.5 w-2.5" /> Verified Provider
                  </span>
                )}
                {vendorBadges[vendor.id] && (
                  <BadgeList badges={vendorBadges[vendor.id]} size="sm" maxDisplay={2} />
                )}
                {getAvailabilityBadge(vendor) && (
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${getAvailabilityBadge(vendor).color}`}>
                    <Clock className="h-2.5 w-2.5" /> {getAvailabilityBadge(vendor).text}
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {(vendor.review_count || 0) > 0 ? (
                  <>
                    <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-medium text-gray-700 ml-1">
                      {vendor.average_rating?.toFixed(1) || '5.0'}
                    </span>
                    <span className="text-gray-300 text-xs ml-1">({vendor.review_count})</span>
                  </>
                ) : (
                  <span className="text-gray-400 text-xs" data-testid="no-reviews-label">No reviews yet</span>
                )}
              </div>
              {displayPrice && (
                <span className="text-emerald-600 text-xs font-medium">{displayPrice}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ─── Render ───
  return (
    <div className="min-h-screen-safe bg-gray-50 pb-20 md:pb-0 flex flex-col">
      <Navigation showFeedback={false} hideMobileNav={true} />

      {/* Event Context Banner */}
      {eventId && returnToEvent && (
        <div className="bg-emerald-50 border-b border-emerald-100">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(`/events/${eventId}`)}
                className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 text-sm font-medium"
                data-testid="back-to-event"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Event
              </button>
              <span className="text-xs text-emerald-600">Adding providers to your event</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Page Body ─── */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">

          {/* Discovery Header */}
          <div className="pt-5 md:pt-8 pb-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900" data-testid="browse-header">
              {headerText}
            </h1>
            <p className="text-sm text-gray-400 mt-0.5" data-testid="trust-signal">
              {trustSignal || 'Find the perfect match for your event'}
            </p>
          </div>

          {/* Vendor Name Search */}
          <div className="relative mb-2.5 mt-3" data-testid="vendor-name-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search providers and services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 h-10 bg-white border-gray-200 text-sm rounded-lg"
              data-testid="vendor-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Mobile: Location pill + Filters button */}
          <div className="flex items-center gap-2 py-3 md:hidden" data-testid="mobile-toolbar">
            {LocationPill()}
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-full hover:bg-gray-50 transition-colors"
              data-testid="mobile-filters-btn"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>

          {/* Category Chips — compact wrap, no horizontal scroll */}
          <div className="pb-6 md:pb-7" data-testid="category-chips">
            <div className="flex flex-wrap gap-2 md:gap-2.5">
              {categoryChips.map((cat) => (
                <button
                  key={cat.value || 'all'}
                  onClick={() => handleGroupChipClick(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    filters.group === cat.value && !filters.category
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                  data-testid={`chip-${cat.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat.label}
                </button>
              ))}
              {moreGroupChips.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        activeMoreGroup
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                      data-testid="chip-more-groups"
                    >
                      {activeMoreGroup ? activeMoreGroup.label : 'More'}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    {moreGroupChips.map((cat) => (
                      <DropdownMenuItem
                        key={cat.value}
                        onClick={() => handleGroupChipClick(cat.value)}
                        className={`text-sm cursor-pointer ${
                          filters.group === cat.value && !filters.category ? 'font-semibold text-gray-900' : 'text-gray-600'
                        }`}
                        data-testid={`more-group-${cat.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {cat.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Two-column layout */}
          <div className="flex gap-8 pb-8">
            {/* ── Desktop Sidebar ── */}
            <aside className="hidden md:block w-60 flex-shrink-0" data-testid="desktop-sidebar">
              <div className="sticky top-20 bg-white rounded-xl border border-gray-100 p-5">
                {FilterPanel({})}
              </div>
            </aside>

            {/* ── Results ── */}
            <div className="flex-1 min-w-0">
              {locationApplied && (
                <div className="hidden md:flex items-center gap-2 mb-4">
                  <button
                    onClick={clearLocationFilter}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-200 hover:bg-emerald-100 transition-colors"
                    data-testid="desktop-location-active"
                  >
                    <MapPin className="h-3 w-3" />
                    Near {locationDisplay}
                    <X className="h-3 w-3 ml-0.5" />
                  </button>
                </div>
              )}

              {loading ? (
                <VendorGridSkeleton count={6} />
              ) : vendors.length === 0 ? (
                <div className="text-center py-16" data-testid="no-vendors">
                  <p className="text-gray-400 text-sm">No providers match your filters.</p>
                  <button
                    onClick={handleClearFilters}
                    className="text-emerald-600 text-sm font-medium mt-2 hover:text-emerald-700"
                    data-testid="clear-filters-link"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="vendor-grid">
                  {vendors.map((vendor) => (
                    <VendorCard key={vendor.id} vendor={vendor} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Mobile Filter Sheet ─── */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="bottom" className="h-[75vh] rounded-t-2xl flex flex-col">
          <div className="px-5 pt-5">
            <SheetHeader className="mb-5">
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription className="sr-only">Filter provider results</SheetDescription>
            </SheetHeader>
          </div>
          <div className="flex-1 overflow-y-auto px-5 pb-4">
            {FilterPanel({ isMobile: true })}
          </div>
          <div className="border-t border-gray-100 px-5 py-3 flex items-center gap-3">
            {(filters.city || filters.state || filters.zip_code || filters.category || filters.group || filters.same_day || filters.next_day) && (
              <button
                onClick={() => { handleClearFilters(); setMobileFiltersOpen(false); }}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                data-testid="sheet-clear-btn"
              >
                Clear
              </button>
            )}
            <Button
              className="ml-auto"
              onClick={() => setMobileFiltersOpen(false)}
              data-testid="sheet-show-results-btn"
            >
              Show results
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── Location Modal ─── */}
      <Dialog open={showLocationModal} onOpenChange={setShowLocationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald-600" />
              Set Your Location
            </DialogTitle>
            <DialogDescription>
              Enter your location or let us detect it automatically.
            </DialogDescription>
          </DialogHeader>

          <button
            onClick={() => { handleDetectLocation(); setShowLocationModal(false); }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors"
            data-testid="modal-detect-location"
          >
            <Locate className="h-4 w-4" />
            Detect my location
          </button>

          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400">or enter manually</span></div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pref-city">City</Label>
              <Input
                id="pref-city"
                placeholder="e.g., Los Angeles"
                value={tempLocation.city}
                onChange={(e) => setTempLocation(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pref-state">State</Label>
              <SearchableSelect
                options={states}
                value={tempLocation.state}
                onValueChange={(v) => setTempLocation(prev => ({ ...prev, state: v }))}
                placeholder="Select state"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pref-zip">Zip Code</Label>
              <Input
                id="pref-zip"
                placeholder="e.g., 90001"
                value={tempLocation.zip}
                onChange={(e) => setTempLocation(prev => ({ ...prev, zip: e.target.value.replace(/\D/g, '') }))}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowLocationModal(false)}>Cancel</Button>
            {user ? (
              <Button onClick={saveLocationPreference} data-testid="save-location-btn">
                <Check className="h-4 w-4 mr-2" /> Save & Apply
              </Button>
            ) : (
              <Button
                onClick={() => {
                  applyLocation(tempLocation.city, tempLocation.state, tempLocation.zip, 'manual');
                  setShowLocationModal(false);
                }}
                data-testid="apply-manual-location-btn"
              >
                <Check className="h-4 w-4 mr-2" /> Apply
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {user && <BottomTabBar />}
      {!user && <Footer />}
    </div>
  );
};

export default BrowseVendors;
